import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyTriggerUsers } from './companyTriggerApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable people list for the Trigger Trace section — ENGAGEMENT ONLY.
 *
 * Every column here is a count or a date: how many triggers somebody logged, on
 * how many separate days, how many they closed, and when they last used the
 * tool. That is the adoption question, and it is a fair one for a company that
 * paid for the benefit to ask.
 *
 * WHAT IS NOT HERE, AND WILL NOT BE: what any of those triggers were, what they
 * felt like, which part of life they came from, or how hard they hit. Those
 * exist in this section only as population figures with nobody's name attached.
 * "Sarah logged 14 triggers" is an adoption fact. "Sarah logged 14 triggers,
 * mostly Rejection, average intensity 8, area Work" is a psychiatric note with
 * her name on it, and the backend has no endpoint that would return the second
 * one. There is no row action and no detail page for the same reason — the
 * arrow every other section's table carries is deliberately absent.
 *
 * Everybody in the population is listed, including people who have never logged
 * anything; they show dashes rather than zeros, so "hasn't used it" reads
 * differently from "used it once and stopped".
 */
const TriggerUsersTable = ({
  fetcher = fetchCompanyTriggerUsers,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const active = row.logs > 0;
          const cell = (value) => (active ? Number(value || 0).toLocaleString() : '-');
          return {
            ...row,
            logsText: cell(row.logs),
            activeDaysText: cell(row.activeDays),
            closedText: cell(row.closed),
            openText: cell(row.pending),
            // Null, not zero, for anybody who has closed nothing — they have
            // not resolved 0% of their triggers, they have not finished any yet.
            resolutionText: row.resolutionRate == null ? '-' : `${row.resolutionRate}%`,
            lastLoggedText: row.lastLogged || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('TriggerUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Logged', key: 'logsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Days used', key: 'activeDaysText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Closed', key: 'closedText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Still open', key: 'openText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Worked through',
      key: 'resolutionText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Last used', key: 'lastLoggedText', sortable: false, align: 'center', type: 'text3' }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

TriggerUsersTable.propTypes = {
  fetcher: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default TriggerUsersTable;
